import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "@/components/common/Logo";
import { useAuth } from "@/contexts/AuthContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const { user, profile } = useAuth();

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-primary shadow-lg">
      <div className="container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="[&_span]:text-primary-foreground">
            <Logo variant="light" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="font-medium text-primary-foreground hover:text-primary-foreground/90 transition-colors">
              Home
            </Link>
            <Link to="/courses" className="font-medium text-primary-foreground hover:text-primary-foreground/90 transition-colors">
              Courses
            </Link>
            <Link to="/how-it-works" className="font-medium text-primary-foreground hover:text-primary-foreground/90 transition-colors">
              How It Works
            </Link>
            <Link to="/earn-money" className="font-medium text-primary-foreground hover:text-primary-foreground/90 transition-colors">
              Earn Money
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="flex items-center gap-3">
                <Button variant="gold">
                  Dashboard
                </Button>
                <Avatar className="w-9 h-9 border-2 border-primary-foreground/20">
                  <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.full_name} />
                  <AvatarFallback className="bg-primary-foreground/20 text-primary-foreground text-sm font-semibold">
                    {profile ? getInitials(profile.full_name) : "U"}
                  </AvatarFallback>
                </Avatar>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="heroOutline">
                    Log In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button variant="gold">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="md:hidden p-2 text-primary-foreground"
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-6 border-t border-primary-foreground/10 bg-primary -mx-4 px-4 animate-fade-up">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-primary-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                Home
              </Link>
              <Link to="/courses" className="text-primary-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                Courses
              </Link>
              <Link to="/how-it-works" className="text-primary-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                How It Works
              </Link>
              <Link to="/earn-money" className="text-primary-foreground font-medium py-2" onClick={() => setIsOpen(false)}>
                Earn Money
              </Link>
              <div className="flex flex-col gap-3 pt-4 border-t border-primary-foreground/10">
                {user ? (
                  <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                    <Button variant="gold" className="w-full">Dashboard</Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button variant="heroOutline" className="w-full">Log In</Button>
                    </Link>
                    <Link to="/signup" onClick={() => setIsOpen(false)}>
                      <Button variant="gold" className="w-full">Get Started</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
